import React, { useState, useEffect } from 'react';
import {
  Box,
  Text,
  HStack,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Badge,
  Card,
  CardHeader,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  Alert,
  AlertIcon,
  Button,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FaTrophy,
  FaFire,
  FaBullseye,
  FaClock,
  FaGamepad,
  FaStar,
  FaBolt,
  FaUsers,
  FaChartLine,
  FaCrown,
  FaEye,
  FaCalendarDay,
  FaMagic,
  FaShieldAlt,
  FaDice,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { ALL_ACHIEVEMENTS, normalizeKey, resolveAchievementDef } from '../../utils/achievements.ts';

interface PlayerStatsProps {
  user: any;
  isOpen?: boolean;
  onClose?: () => void;
}

// Avoid relying on Node's process types in the browser
// @ts-ignore
const API_URL = process.env.REACT_APP_API_URL || '';

interface UserStats {
  // Statistiques principales
  total_games: number;
  local_games: number;
  multiplayer_games: number;
  games_won: number;
  total_correct_answers: number;
  total_questions: number;
  best_streak: number;
  current_streak: number;
  total_play_time: number;
  categories_completed: number;
  mystery_attacks_successful: number;
  mystery_attacks_total: number;
  power_ups_used: number;
  mystery_wheels_triggered: number;
  level: number;
  xp: number;
  
  // Statistiques calculées
  averageAccuracy: number;
  winRate: number;
  mysterySuccessRate: number;
  xpToNextLevel: number;
  
  // Données annexes
  favoriteCategory: {
    name: string;
    accuracy: number;
    gamesPlayed: number;
  } | null;
  categoryStats: Array<{
    category_key: string;
    category_name: string;
    questions_answered: number;
    correct_answers: number;
    games_played: number;
    accuracy: number;
  }>;
  achievements: Array<{
    name: string;
    earnedAt: string;
  }>;
  mysteryPowers: Array<{
    power_type: string;
    times_triggered: number;
    times_received: number;
  }>;
  mostTriggeredPower: {
    type: string;
    times: number;
    name: string;
    icon: string;
    description?: string;
  } | null;
  mostReceivedPower: {
    type: string;
    times: number;
    name: string;
    icon: string;
    description?: string;
  } | null;
  last_played_date: string;
  smartClash?: {
    attempts: number;
    created: number;
    totalPoints: number;
    categoriesPlayed: number;
    best: { points: number; correct: number; timeMs: number; submittedAt: string | null } | null;
    lastPlayedAt: string | null;
  } | null;
  dailyStreak?: {
    daysPlayed: number;
    bestScore: number;
    bestDurationSec: number;
    lastPlayedAt: string | null;
    todayPlayed: boolean;
  } | null;
}

interface RecentGame {
  game_mode: 'local' | 'multiplayer';
  game_type: 'private' | 'public';
  won: 0 | 1 | boolean;
  duration_minutes: number;
  correct_answers: number;
  total_questions: number;
  max_streak: number;
  categories_completed: number;
  mystery_attacks_used: number;
  mystery_attacks_successful: number;
  power_ups_used: number;
  xp_gained: number;
  played_at: string;
}

// Hook pour charger les statistiques depuis l'API
const usePlayerStats = (user: any): { stats: UserStats | null; loading: boolean; error: string | null; refetch: () => void } => {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentGames, setRecentGames] = useState<RecentGame[] | null>(null);

  const fetchStats = async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = await user.getIdToken();
  const response = await fetch(`${API_URL}/api/user/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des statistiques');
      }

      const data = await response.json();
      setStats(data);

      // Charger les parties récentes en parallèle
      try {
        const recentRes = await fetch(`${API_URL}/api/user/recent-games?limit=10`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (recentRes.ok) {
          const recentData = await recentRes.json();
          setRecentGames(recentData.games || []);
        } else {
          setRecentGames([]);
        }
      } catch {
        setRecentGames([]);
      }
    } catch (err) {
      console.error('❌ Erreur lors du chargement des stats:', err);
      setError('Impossible de charger les statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [user]);

  return { stats, loading, error, refetch: fetchStats };
};

// Fonction pour obtenir l'image du pouvoir
const getPowerImage = (powerType: string): string => {
  const powerImages: { [key: string]: string } = {
    'plus1': 'plus1.png',
    'plus2': 'plus2.png',
    'plus3': 'plus3.png',
    'minus1': 'minus1.png',
    'minus2': 'minus2.png',
    'minus3': 'minus3.png',
    'swap1': 'swap1.png',
    'swap2': 'swap2.png',
    'swap3': 'swap3.png',
    'blind': 'blind.png',
    'blocked': 'bloked.png', // Note: fichier est nommé "bloked" dans le dossier
    'hardcore': 'hardcore.png',
    'translate': 'translate.png',
    'nuke': 'nuke.png'
  };
  
  const imageName = powerImages[powerType];
  return imageName ? `/power/${imageName}` : '/power/plus1.png'; // fallback
};

// Fonctions pour les taunts personnalisés
const getTriggeredPowerTaunt = (powerType: string, times: number): string => {
  if (times === 0) return "🎰 En attente";
  
  const taunts: { [key: string]: string[] } = {
    'plus1': ["🧸 Vous attaquez avec des nounours !", "💝 Généreux comme un père Noël !", "🌈 Spécialiste des câlins magiques !"],
    'plus2': ["🎁 Double dose de gentillesse !", "💖 Cœur tendre, pouvoirs doux !", "🦄 Licorne des bonnes actions !"],
    'plus3': ["🍭 Roi/Reine des sucreries !", "👼 Ange gardien du multijoueur !", "🌸 Distributeur de bonheur !"],
    'minus1': ["⚔️ Guerrier sournois !", "🐍 Serpent venimeux !", "💀 Faucheuse amateur !"],
    'minus2': ["🔥 Démon des statistiques !", "⚡ Tonnerre destructeur !", "🗡️ Épée affûtée !"],
    'minus3': ["💣 Bombe à retardement !", "🌪️ Tornade dévastatrice !", "⚰️ Fossoyeur professionnel !"],
    'swap1': ["🦹 Pickpocket mystique !", "🔄 Voleur de talents !", "🎭 Magicien du larcin !"],
    'swap2': ["🕷️ Araignée voleuse !", "💎 Pirate des streaks !", "🦅 Rapace calculateur !"],
    'swap3': ["👑 Empereur du vol !", "🐉 Dragon cupide !", "💰 Bandit légendaire !"],
    'blind': ["👻 Fantôme de la confusion !", "🌫️ Maître des illusions !", "🎪 Magicien de l'ombre !"],
    'blocked': ["🚧 Architecte du chaos !", "🔒 Gardien des interdictions !", "⛔ Dictateur des catégories !"],
    'hardcore': ["💀 Bourreau impitoyable !", "🔥 Démon de la difficulté !", "⚡ Maître du cauchemar !"],
    'translate': ["🌍 Babel incarné !", "🗣️ Polyglotte maléfique !", "📚 Bibliothèque maudite !"],
    'nuke': ["☢️ Arme de destruction massive !", "💥 Apocalypse incarnée !", "🌋 Volcan de la reset !"]
  };
  
  const powerTaunts = taunts[powerType] || ["✨ Mystérieux comme toujours !"];
  const intensity = times > 10 ? 2 : times > 5 ? 1 : 0;
  return powerTaunts[Math.min(intensity, powerTaunts.length - 1)];
};

const getReceivedPowerTaunt = (powerType: string, times: number): string => {
  if (times === 0) return "🛡️ Invulnérable";
  
  const taunts: { [key: string]: string[] } = {
    'plus1': ["🍯 Aimant à gentillesse !", "🌻 Attirez les bonnes âmes !", "🧲 Pôle positif naturel !"],
    'plus2': ["💫 Étoile porte-bonheur !", "🌈 Arc-en-ciel vivant !", "🎈 Ballon de joie !"],
    'plus3': ["👑 Royauté bienveillante !", "🦄 Licorne apprivoisée !", "✨ Fée marraine personnelle !"],
    'minus1': ["🎯 Cible de choix !", "🩸 Victime récurrente !", "💔 Cœur brisé professionnel !"],
    'minus2': ["⚰️ Cercueil ambulant !", "🌪️ Aspirateur à malheur !", "🔥 Brasier de souffrance !"],
    'minus3': ["💀 Mort-vivant statistique !", "🕳️ Trou noir du scoring !", "⚡ Paratonnerre à malédictions !"],
    'swap1': ["💰 Banque ambulante !", "🎁 Cadeau sur pattes !", "🍯 Pot de miel convoité !"],
    'swap2': ["💎 Trésor vivant !", "🏆 Trophée mobile !", "💰 Coffre-fort ouvert !"],
    'swap3': ["👑 Couronne convoitée !", "🌟 Étoile filante à voler !", "💫 Galaxie de richesses !"],
    'blind': ["🎭 Mystère ambulant !", "🌫️ Brouillard humain !", "👻 Fantôme visible !"],
    'blocked': ["🔒 Cadenas humain !", "⛔ Panneau stop vivant !", "🚧 Barrière mobile !"],
    'hardcore': ["💀 Martyr volontaire !", "🔥 Salamandre résistante !", "⚡ Paratonnerre héroïque !"],
    'translate': ["🗺️ Atlas vivant !", "📚 Dictionnaire ambulant !", "🌍 Tour de Babel !"],
    'nuke': ["☢️ Zone de guerre !", "💥 Point d'impact !", "🌋 Épicentre du chaos !"]
  };
  
  const powerTaunts = taunts[powerType] || ["🎯 Victime mystérieuse !"];
  const intensity = times > 8 ? 2 : times > 4 ? 1 : 0;
  return powerTaunts[Math.min(intensity, powerTaunts.length - 1)];
};

// Modal pour afficher les détails des catégories
const StatsModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  stats: UserStats;
  user: any;
}> = ({ isOpen, onClose, stats, user }) => {
  // Theme tokens for Achievements (dark-mode friendly)
  const achvCardBg = useColorModeValue('white', 'gray.700');
  const achvLockedBg = useColorModeValue('gray.50', 'gray.800');
  const achvTextMuted = useColorModeValue('gray.600', 'gray.300');
  const achvBorderUnlocked = useColorModeValue('green.300', 'green.500');
  const achvBorderLocked = useColorModeValue('gray.200', 'gray.600');

  const formatPlayTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${remainingMinutes}m`;
  };

  const getLevelTitle = (level: number): string => {
    if (level < 5) return 'Novice';
    if (level < 10) return 'Apprenti';
    if (level < 15) return 'Expert';
    if (level < 20) return 'Maître';
    if (level < 30) return 'Grand Maître';
    return 'Légende';
  };

  // Valeurs dérivées sûres pour éviter NaN/undefined
  const totalAttacks = stats.mystery_attacks_total || 0;
  const successfulAttacks = stats.mystery_attacks_successful || 0;
  const successRate = totalAttacks > 0 ? (successfulAttacks / totalAttacks) * 100 : 0;
  const totalReceived = (stats.mysteryPowers || []).reduce((sum, p) => sum + (p.times_received || 0), 0);
  // Nouveaux indicateurs pour distinguer les 3 cercles
  const avgAttacksPerGame = stats.total_games > 0 ? totalAttacks / stats.total_games : 0; // attaques par partie
  const activityPercent = Math.min(Math.round((avgAttacksPerGame / 5) * 100), 100); // 5 attaques/partie = 100%
  const pressurePercent = (totalReceived + totalAttacks) > 0 
    ? Math.round((totalReceived / (totalReceived + totalAttacks)) * 100) 
    : 0; // % reçues parmi total (reçues + lancées)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Icon as={FaChartLine} color="blue.500" />
            <Text>📊 Statistiques de {user?.displayName || 'Joueur'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} overflowY="auto">
          <Tabs variant="enclosed" colorScheme="blue">
            <TabList>
              <Tab>📈 Résumé</Tab>
              <Tab>🏆 Achievements</Tab>
              <Tab>⚡ SmartClash</Tab>
              <Tab>🏆 Daily Streak</Tab>
              <Tab>📊 Par Catégorie</Tab>
              <Tab>🎮 Parties Récentes</Tab>
            </TabList>

            <TabPanels>
              {/* Onglet Résumé */}
              <TabPanel>
                <VStack spacing={6}>
                  {/* Progression niveau */}
                  <Card w="full">
                    <CardHeader>
                      <HStack>
                        <Icon as={FaCrown} color="yellow.400" />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="lg" fontWeight="bold">
                            Niveau {stats.level} - {getLevelTitle(stats.level)}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {stats.xp} XP total
                          </Text>
                        </VStack>
                        <Badge colorScheme="purple" p={2} ml="auto">
                          {stats.xpToNextLevel} XP jusqu'au niveau {stats.level + 1}
                        </Badge>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0}>
                      <Progress 
                        value={((stats.xp) / (stats.xp + stats.xpToNextLevel)) * 100} 
                        size="lg" 
                        colorScheme="purple" 
                        borderRadius="full"
                      />
                    </CardBody>
                  </Card>

                  {/* Statistiques principales */}
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
                    <Stat>
                      <StatLabel><Icon as={FaGamepad} mr={2} />Parties</StatLabel>
                      <StatNumber>{stats.total_games}</StatNumber>
                      <StatHelpText>{stats.games_won} victoires ({stats.winRate}%)</StatHelpText>
                    </Stat>
                    
                    <Stat>
                      <StatLabel><Icon as={FaBullseye} mr={2} />Précision</StatLabel>
                      <StatNumber>{stats.averageAccuracy}%</StatNumber>
                      <StatHelpText>{stats.total_correct_answers}/{stats.total_questions}</StatHelpText>
                    </Stat>
                    
                    <Stat>
                      <StatLabel><Icon as={FaFire} mr={2} />Série</StatLabel>
                      <StatNumber>{stats.best_streak}</StatNumber>
                      <StatHelpText>Actuelle: {stats.current_streak}</StatHelpText>
                    </Stat>
                    
                    <Stat>
                      <StatLabel><Icon as={FaClock} mr={2} />Temps</StatLabel>
                      <StatNumber>{formatPlayTime(stats.total_play_time)}</StatNumber>
                      <StatHelpText>Temps total</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  {/* Performance mystère réinventée */}
                  <Card w="full" bg="linear-gradient(135deg, purple.50, indigo.50)" borderWidth="2px" borderColor="purple.300">
                    <CardHeader>
                      <HStack>
                        <Icon as={FaMagic} color="purple.600" boxSize={6} />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="xl" fontWeight="bold" color="white" textShadow="2px 2px 4px rgba(0,0,0,0.5)">
                            🎭 Arsenal Mystère
                          </Text>
                          <Text fontSize="sm" color="purple.100" fontWeight="semibold" textShadow="1px 1px 2px rgba(0,0,0,0.3)">
                            Analyse détaillée de vos batailles magiques
                          </Text>
                        </VStack>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      {/* Statistiques principales en camemberts */}
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
                        {/* 1) Taux de réussite (qualité) */}
                        <VStack spacing={4}>
                          <Box position="relative">
                            <CircularProgress 
                              value={successRate}
                              size="140px" 
                              thickness="12px"
                              color="green.400"
                              trackColor="red.200"
                            >
                              <CircularProgressLabel fontSize="xl" fontWeight="bold" color="green.600">
                                {Math.round(successRate)}%
                              </CircularProgressLabel>
                            </CircularProgress>
                          </Box>
                          <VStack spacing={1} textAlign="center">
                            <Text fontWeight="bold" color="green.600" fontSize="lg">Taux de Réussite</Text>
                            <Text fontSize="sm" color="gray.600">
                              {successfulAttacks} réussies / {totalAttacks} tentées
                            </Text>
                            <Badge colorScheme={totalAttacks > 0 && successfulAttacks / totalAttacks >= 0.7 ? "green" : totalAttacks > 0 && successfulAttacks / totalAttacks >= 0.4 ? "yellow" : "red"} variant="solid" px={3} py={1}>
                              {totalAttacks > 0 && successfulAttacks / totalAttacks >= 0.8 ? "🎯 Sniper Mystique" : 
                               totalAttacks > 0 && successfulAttacks / totalAttacks >= 0.6 ? "⚡ Tireur d'Élite" :
                               totalAttacks > 0 && successfulAttacks / totalAttacks >= 0.4 ? "🎲 Apprenti" : "🌫️ Nébuleux"}
                            </Badge>
                          </VStack>
                        </VStack>

                        {/* 2) Intensité d'activité (volume): Attaques par partie */}
                        <VStack spacing={4}>
                          <Box position="relative">
                            <CircularProgress 
                              value={activityPercent}
                              size="140px" 
                              thickness="12px"
                              color="blue.400"
                              trackColor="gray.200"
                            >
                              <CircularProgressLabel fontSize="xl" fontWeight="bold" color="blue.600">
                                {avgAttacksPerGame.toFixed(1)}
                              </CircularProgressLabel>
                            </CircularProgress>
                          </Box>
                          <VStack spacing={2} textAlign="center">
                            <Text fontWeight="bold" color="blue.600" fontSize="lg">Intensité d'Activité</Text>
                            <Text fontSize="sm" color="gray.600">{totalAttacks} attaques au total</Text>
                            <Badge colorScheme={avgAttacksPerGame >= 4 ? "blue" : avgAttacksPerGame >= 2 ? "cyan" : "gray"} variant="outline">
                              {avgAttacksPerGame >= 5 ? "🌪️ Très Actif" : 
                               avgAttacksPerGame >= 3 ? "⚔️ Actif" :
                               avgAttacksPerGame >= 1 ? "🗡️ Régulier" : "🛡️ Pacifique"}
                            </Badge>
                          </VStack>
                        </VStack>

                        {/* 3) Pression adverse (% reçues vs total échangé) */}
                        <VStack spacing={4}>
                          <Box position="relative">
                            <CircularProgress 
                              value={pressurePercent}
                              size="140px" 
                              thickness="12px"
                              color="orange.400"
                              trackColor="orange.100"
                            >
                              <CircularProgressLabel fontSize="xl" fontWeight="bold" color="orange.600">
                                {pressurePercent}%
                              </CircularProgressLabel>
                            </CircularProgress>
                          </Box>
                          <VStack spacing={1} textAlign="center">
                            <Text fontWeight="bold" color="orange.600" fontSize="lg">Pression Adverse</Text>
                            <Text fontSize="sm" color="gray.600">{totalReceived} reçues vs {totalAttacks} lancées</Text>
                            <Badge colorScheme={pressurePercent >= 60 ? "orange" : pressurePercent >= 35 ? "yellow" : "gray"} variant="solid" px={3} py={1}>
                              {pressurePercent >= 70 ? "🎯 Cible Premium" : 
                               pressurePercent >= 50 ? "⚡ Sous Pression" :
                               pressurePercent >= 30 ? "�️ Équilibré" : "👻 Discret"}
                            </Badge>
                          </VStack>
                        </VStack>
                      </SimpleGrid>

                      {/* Section des pouvoirs préférés */}
                      <Divider borderColor="purple.200" mb={6} />
                      
                      <Text fontSize="lg" fontWeight="bold" color="purple.400" mb={4} textAlign="center" textShadow="1px 1px 2px rgba(255,255,255,0.5)">
                        🎰 Pouvoirs de la Roulette Mystère
                      </Text>
                      
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        {/* Pouvoir le plus déclenché */}
                        <Card bg="gradient-to-br from-green.50 to-emerald.50" borderWidth="2px" borderColor="green.200">
                          <CardHeader pb={2}>
                            <HStack>
                              <Icon as={FaDice} color="green.500" boxSize={5} />
                              <Text fontWeight="bold" color="green.400">Pouvoir le Plus Émis</Text>
                            </HStack>
                          </CardHeader>
                          <CardBody>
                            {stats.mostTriggeredPower ? (
                              <VStack spacing={3}>
                                <Badge colorScheme="green" variant="subtle" px={3}>
                                    {getTriggeredPowerTaunt(stats.mostTriggeredPower.type, stats.mostTriggeredPower.times)}
                                  </Badge>
                                <Box 
                                  bg="green.100" 
                                  p={4} 
                                  borderRadius="full"
                                  border="3px solid"
                                  borderColor="green.300"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                  <img 
                                    src={getPowerImage(stats.mostTriggeredPower.type)}
                                    alt={stats.mostTriggeredPower.name}
                                    style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                  />
                                </Box>
                                <VStack spacing={1} textAlign="center">
                                  <Text fontSize="lg" fontWeight="bold" color="green.400">
                                    {stats.mostTriggeredPower.name}
                                  </Text>
                                  {stats.mostTriggeredPower.description && (
                                    <Text fontSize="xs" color="green.300" textAlign="center" px={2}>
                                      {stats.mostTriggeredPower.description}
                                    </Text>
                                  )}
                                  <Badge 
                                    colorScheme="green" 
                                    variant="solid" 
                                    fontSize="sm" 
                                    fontWeight="bold"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                  >
                                    🎯 Émis {stats.mostTriggeredPower.times} fois
                                  </Badge>
                                  
                                </VStack>
                              </VStack>
                            ) : (
                              <VStack spacing={3} py={4}>
                                <Box 
                                  bg="gray.100" 
                                  p={4} 
                                  borderRadius="full"
                                  border="3px solid"
                                  borderColor="gray.300"
                                >
                                  <Text fontSize="2xl">❓</Text>
                                </Box>
                                <VStack spacing={1} textAlign="center">
                                  <Text fontSize="lg" fontWeight="bold" color="gray.500">
                                    Aucune donnée
                                  </Text>
                                  <Text fontSize="sm" color="gray.400">
                                    Pas encore de roulette émise
                                  </Text>
                                  <Badge colorScheme="gray" variant="subtle" px={3}>
                                    🎰 En attente
                                  </Badge>
                                </VStack>
                              </VStack>
                            )}
                          </CardBody>
                        </Card>

                        {/* Pouvoir le plus subi */}
                        <Card bg="gradient-to-br from-red.50 to-orange.50" borderWidth="2px" borderColor="red.200">
                          <CardHeader pb={2}>
                            <HStack>
                              <Icon as={FaShieldAlt} color="red.500" boxSize={5} />
                              <Text fontWeight="bold" color="red.400">Pouvoir le Plus Subi</Text>
                            </HStack>
                          </CardHeader>
                          <CardBody>
                            {stats.mostReceivedPower ? (
                              <VStack spacing={3}>
                                <Badge colorScheme="red" variant="subtle" px={3}>
                                    {getReceivedPowerTaunt(stats.mostReceivedPower.type, stats.mostReceivedPower.times)}
                                  </Badge>
                                <Box 
                                  bg="red.100" 
                                  p={4} 
                                  borderRadius="full"
                                  border="3px solid"
                                  borderColor="red.300"
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                >
                                    
                                  <img 
                                    src={getPowerImage(stats.mostReceivedPower.type)}
                                    alt={stats.mostReceivedPower.name}
                                    style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                                  />
                                </Box>
                                <VStack spacing={1} textAlign="center">
                                  <Text fontSize="lg" fontWeight="bold" color="red.400">
                                    {stats.mostReceivedPower.name}
                                  </Text>
                                  {stats.mostReceivedPower.description && (
                                    <Text fontSize="xs" color="red.300" textAlign="center" px={2}>
                                      {stats.mostReceivedPower.description}
                                    </Text>
                                  )}
                                  <Badge 
                                    colorScheme="red" 
                                    variant="solid" 
                                    fontSize="sm" 
                                    fontWeight="bold"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                  >
                                    🛡️ Subi {stats.mostReceivedPower.times} fois
                                  </Badge>
                                  
                                </VStack>
                              </VStack>
                            ) : (
                              <VStack spacing={3} py={4}>
                                <Box 
                                  bg="gray.100" 
                                  p={4} 
                                  borderRadius="full"
                                  border="3px solid"
                                  borderColor="gray.300"
                                >
                                  <Text fontSize="2xl">🛡️</Text>
                                </Box>
                                <VStack spacing={1} textAlign="center">
                                  <Text fontSize="lg" fontWeight="bold" color="gray.500">
                                    Aucune attaque
                                  </Text>
                                  <Text fontSize="sm" color="gray.400">
                                    Jamais visé par les adversaires
                                  </Text>
                                  <Badge colorScheme="gray" variant="subtle" px={3}>
                                    🛡️ Forteresse impénétrable
                                  </Badge>
                                  <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
                                    "Personne n'ose vous défier !"
                                  </Text>
                                </VStack>
                              </VStack>
                            )}
                          </CardBody>
                        </Card>
                      </SimpleGrid>

                      {/* Statistiques rapides en bas */}
                      <Divider borderColor="purple.200" my={6} />
                      
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat textAlign="center" bg="green.50" p={3} borderRadius="lg" borderWidth="1px" borderColor="green.200">
                          <StatLabel color="green.600" fontSize="xs" fontWeight="bold">PRÉCISION</StatLabel>
                          <StatNumber color="green.700" fontSize="xl">{Math.round(successRate)}%</StatNumber>
                          <StatHelpText color="green.500" fontSize="xs">taux de réussite</StatHelpText>
                        </Stat>
                        
                        <Stat textAlign="center" bg="blue.50" p={3} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
                          <StatLabel color="blue.600" fontSize="xs" fontWeight="bold">ACTIVITÉ</StatLabel>
                          <StatNumber color="blue.700" fontSize="xl">{totalAttacks}</StatNumber>
                          <StatHelpText color="blue.500" fontSize="xs">attaques lancées</StatHelpText>
                        </Stat>
                        
                        <Stat textAlign="center" bg="orange.50" p={3} borderRadius="lg" borderWidth="1px" borderColor="orange.200">
                          <StatLabel color="orange.600" fontSize="xs" fontWeight="bold">RÉSISTANCE</StatLabel>
                          <StatNumber color="orange.700" fontSize="xl">{totalReceived}</StatNumber>
                          <StatHelpText color="orange.500" fontSize="xs">attaques subies</StatHelpText>
                        </Stat>
                        
                        <Stat textAlign="center" bg="purple.50" p={3} borderRadius="lg" borderWidth="1px" borderColor="purple.200">
                          <StatLabel color="purple.600" fontSize="xs" fontWeight="bold">ROULETTE</StatLabel>
                          <StatNumber color="purple.700" fontSize="xl">{stats.mystery_wheels_triggered || stats.power_ups_used}</StatNumber>
                          <StatHelpText color="purple.500" fontSize="xs">roulettes déclenchées</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Onglet Achievements */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {(() => {
                    // Build a fast lookup of unlocked keys from server by normalizing names
                    const unlockedSet = new Set((stats.achievements || []).map(a => normalizeKey(a.name)));
                    const knownKeys = new Set(ALL_ACHIEVEMENTS.map(def => normalizeKey(def.id) || normalizeKey(def.title)));

                    // Map known definitions with unlock state
                    const catalogItems = ALL_ACHIEVEMENTS.map(def => {
                      const key = normalizeKey(def.id) || normalizeKey(def.title);
                      const isUnlocked = unlockedSet.has(key);
                      const unlockedAt = (stats.achievements || []).find(a => normalizeKey(a.name) === key)?.earnedAt;
                      return { ...def, isUnlocked, unlockedAt } as typeof def & { isUnlocked: boolean; unlockedAt?: string };
                    });

                    // Create items for unknown achievements coming from backend
                    const unknownUnlocked = (stats.achievements || [])
                      .filter(a => !knownKeys.has(normalizeKey(a.name)))
                      .map(a => {
                        const resolved = resolveAchievementDef(a.name);
                        return {
                          id: normalizeKey(a.name),
                          title: resolved?.title || a.name,
                          emoji: resolved?.emoji || '🏆',
                          description: resolved?.description || 'Trophée spécial débloqué !',
                          colorScheme: (resolved as any)?.colorScheme || 'yellow',
                          rarity: (resolved as any)?.rarity || 'common',
                          isUnlocked: true,
                          unlockedAt: a.earnedAt,
                        };
                      });

                    const items = [...catalogItems, ...unknownUnlocked];
                    const unlockedCount = items.filter(i => i.isUnlocked).length;

                    return (
                      <>
                        <HStack justify="space-between" align="center">
                          <Text fontSize="lg" fontWeight="bold">🏆 Vos Achievements</Text>
                          <Badge colorScheme="purple" variant="subtle" px={3} py={1} borderRadius="full">
                            {unlockedCount}/{items.length} débloqués
                          </Badge>
                        </HStack>

                        {items.length ? (
                          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4} w="full">
                            {items
                              .slice()
                              .sort((a,b) => (a.isUnlocked === b.isUnlocked) ? 0 : a.isUnlocked ? -1 : 1)
                              .map((it) => (
                                <Box key={it.id} position="relative">
                                  {/* Corner ribbon for rarity */}
                                  <Box
                                    position="absolute"
                                    top={2}
                                    right={-6}
                                    transform="rotate(45deg)"
                                    bg={(it as any).rarity === 'legendary' ? 'purple.500' : (it as any).rarity === 'epic' ? 'pink.500' : (it as any).rarity === 'rare' ? 'blue.500' : 'gray.500'}
                                    color="white"
                                    px={6}
                                    py={0.5}
                                    fontSize="xs"
                                    fontWeight="bold"
                                    shadow="md"
                                    borderRadius="md"
                                  >
                                    {(it as any).rarity === 'legendary' ? 'LEG' : (it as any).rarity === 'epic' ? 'EPI' : (it as any).rarity === 'rare' ? 'RAR' : 'COM'}
                                  </Box>

                                  <Card
                                    variant="outline"
                                    bg={it.isUnlocked ? achvCardBg : achvLockedBg}
                                    borderColor={it.isUnlocked ? achvBorderUnlocked : achvBorderLocked}
                                    opacity={it.isUnlocked ? 1 : 0.75}
                                    overflow="hidden"
                                  >
                                    {/* Locked diagonal stripes overlay */}
                                    {!it.isUnlocked && (
                                      <Box
                                        position="absolute"
                                        inset={0}
                                        pointerEvents="none"
                                        bgGradient="repeating-linear(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 8px, rgba(0,0,0,0.06) 8px, rgba(0,0,0,0.06) 16px)"
                                      />
                                    )}

                                    <CardBody>
                                      <HStack align="start" spacing={3}>
                                        <Box fontSize="2xl" lineHeight={1} filter={it.isUnlocked ? undefined : 'grayscale(100%)'}>
                                          {it.emoji}
                                        </Box>
                                        <VStack align="start" spacing={1} flex={1}>
                                          <HStack>
                                            <Text fontWeight="bold">{it.title}</Text>
                                            <Badge colorScheme={(it as any).colorScheme || 'gray'} variant={it.isUnlocked ? 'solid' : 'outline'}>
                                              {(it as any).rarity === 'legendary' ? 'Légendaire' : (it as any).rarity === 'epic' ? 'Épique' : (it as any).rarity === 'rare' ? 'Rare' : 'Commun'}
                                            </Badge>
                                          </HStack>
                                          <Text fontSize="sm" color={achvTextMuted}>{(it as any).description}</Text>
                                          {it.isUnlocked ? (
                                            <Badge colorScheme="green" variant="subtle" mt={1} borderRadius="full" px={2}>
                                              ✅ Obtenu le {(it as any).unlockedAt ? new Date((it as any).unlockedAt).toLocaleDateString('fr-FR') : '—'}
                                            </Badge>
                                          ) : (
                                            <HStack spacing={2} mt={1}>
                                              <Badge colorScheme="gray" variant="subtle" borderRadius="full" px={2}>
                                                🔒 Non débloqué
                                              </Badge>
                                              <Text fontSize="xs" color={achvTextMuted}>
                                                Astuce: jouez et relevez des défis pour le débloquer
                                              </Text>
                                            </HStack>
                                          )}
                                        </VStack>
                                      </HStack>
                                    </CardBody>
                                  </Card>

                                  {/* Big lock icon overlay for locked */}
                                  {!it.isUnlocked && (
                                    <Box position="absolute" top={2} left={2} opacity={0.3} fontSize="lg">🔒</Box>
                                  )}
                                </Box>
                              ))}
                          </SimpleGrid>
                        ) : (
                          <Card>
                            <CardBody textAlign="center" py={8}>
                              <Icon as={FaTrophy} boxSize={12} color="gray.300" mb={4} />
                              <Text color="gray.500">Aucun achievement disponible</Text>
                            </CardBody>
                          </Card>
                        )}
                      </>
                    );
                  })()}
                </VStack>
              </TabPanel>

              {/* Onglet SmartClash */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {stats.smartClash ? (
                    <>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaBolt} mr={2} />Tentatives</StatLabel>
                          <StatNumber>{stats.smartClash.attempts}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaGamepad} mr={2} />Défis créés</StatLabel>
                          <StatNumber>{stats.smartClash.created}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaBullseye} mr={2} />Points cumulés</StatLabel>
                          <StatNumber>{stats.smartClash.totalPoints}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaStar} mr={2} />Catégories jouées</StatLabel>
                          <StatNumber>{stats.smartClash.categoriesPlayed}</StatNumber>
                        </Stat>
                      </SimpleGrid>

                      <Card mt={4} variant="outline">
                        <CardHeader>
                          <HStack>
                            <Icon as={FaTrophy} color="yellow.400" />
                            <Text fontWeight="bold">Meilleure performance</Text>
                          </HStack>
                        </CardHeader>
                        <CardBody>
                          {stats.smartClash.best ? (
                            <HStack spacing={6} wrap="wrap">
                              <Badge colorScheme="yellow">Points {stats.smartClash.best.points}</Badge>
                              <Badge colorScheme="green">Bonnes {stats.smartClash.best.correct}</Badge>
                              <Badge colorScheme="blue">Temps {(stats.smartClash.best.timeMs/1000).toFixed(1)}s</Badge>
                              {stats.smartClash.best.submittedAt && (
                                <Text fontSize="sm" color="gray.500">le {new Date(stats.smartClash.best.submittedAt).toLocaleDateString('fr-FR')}</Text>
                              )}
                            </HStack>
                          ) : (
                            <Text color="gray.500">Aucune performance enregistrée</Text>
                          )}
                        </CardBody>
                      </Card>
                    </>
                  ) : (
                    <Alert status="info"><AlertIcon />Aucune donnée SmartClash</Alert>
                  )}

                  {/* Goofy: Recent SyncDuel recaps (server) */}
                  <Card mt={6} variant="outline">
                    <CardHeader>
                      <HStack>
                        <Icon as={FaUsers} color="purple.400" />
                        <Text fontWeight="bold">Dernières parties SyncDuel</Text>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <ServerSyncDuelRecents user={user} />
                    </CardBody>
                  </Card>
                </VStack>
              </TabPanel>

              {/* Onglet Daily Streak */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {stats.dailyStreak ? (
                    <>
                      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaCalendarDay} mr={2} />Jours joués</StatLabel>
                          <StatNumber>{stats.dailyStreak.daysPlayed}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaFire} mr={2} />Meilleur score</StatLabel>
                          <StatNumber>{stats.dailyStreak.bestScore}</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaClock} mr={2} />Meilleur temps</StatLabel>
                          <StatNumber>{stats.dailyStreak.bestDurationSec}s</StatNumber>
                        </Stat>
                        <Stat textAlign="center">
                          <StatLabel><Icon as={FaEye} mr={2} />Aujourd'hui</StatLabel>
                          <StatNumber>{stats.dailyStreak.todayPlayed ? '✔︎' : '—'}</StatNumber>
                        </Stat>
                      </SimpleGrid>

                      {stats.dailyStreak.lastPlayedAt && (
                        <Text fontSize="sm" color="gray.500">Dernière participation: {new Date(stats.dailyStreak.lastPlayedAt).toLocaleString('fr-FR')}</Text>
                      )}
                    </>
                  ) : (
                    <Alert status="info"><AlertIcon />Aucune donnée Daily Streak</Alert>
                  )}
                </VStack>
              </TabPanel>

              {/* Onglet Par Catégorie */}
              <TabPanel>
                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>📊 Statistiques par Catégorie</Text>
                  
                  {stats.favoriteCategory && (
                    <Card w="full" variant="outline" bg="blue.50">
                      <CardBody>
                        <HStack>
                          <Icon as={FaStar} color="yellow.400" boxSize={6} />
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" color="blue.600">
                              Catégorie favorite: {stats.favoriteCategory.name}
                            </Text>
                            <Text fontSize="sm" color="gray.600">
                              {stats.favoriteCategory.accuracy}% de précision • {stats.favoriteCategory.gamesPlayed} parties
                            </Text>
                          </VStack>
                        </HStack>
                      </CardBody>
                    </Card>
                  )}

                  <TableContainer w="full">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Catégorie</Th>
                          <Th isNumeric>Parties</Th>
                          <Th isNumeric>Questions</Th>
                          <Th isNumeric>Bonnes réponses</Th>
                          <Th isNumeric>Précision</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.categoryStats.map((cat, index) => (
                          <Tr key={index}>
                            <Td fontWeight="medium">{cat.category_name || cat.category_key}</Td>
                            <Td isNumeric>{cat.games_played}</Td>
                            <Td isNumeric>{cat.questions_answered}</Td>
                            <Td isNumeric>{cat.correct_answers}</Td>
                            <Td isNumeric>
                              <Badge colorScheme={cat.accuracy >= 80 ? 'green' : cat.accuracy >= 60 ? 'yellow' : 'red'}>
                                {cat.accuracy}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </VStack>
              </TabPanel>

        {/* Onglet Parties Récentes */}
              <TabPanel>
                <VStack spacing={4}>
                  <Text fontSize="lg" fontWeight="bold" mb={4}>🎮 Parties Récentes</Text>
          {/* Liste des dernières parties */}
          <RecentGamesList user={user} />
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};


// Composant principal - Maintenant simplement la modale complète
const PlayerStats: React.FC<PlayerStatsProps> = ({ user, isOpen = false, onClose }) => {
  const { t } = useTranslation();
  const { stats, loading, error, refetch } = usePlayerStats(user);
  
  // React hooks must be called at the top level
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Si pas de fonction onClose fournie, ne pas afficher la modale
  if (!onClose || !isOpen) {
    return null;
  }

  // Si utilisateur non authentifié, ne pas afficher
  if (!user || user.isAnonymous) {
    return null;
  }

  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>❌ Erreur</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Alert status="error" mb={4}>
              <AlertIcon />
              <Text fontSize="sm">Erreur lors du chargement des statistiques</Text>
            </Alert>
            <Button onClick={refetch} w="full">
              Réessayer
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  if (loading || !stats) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            <HStack>
              <Icon as={FaChartLine} color="blue.500" />
              <Text>📊 Chargement des statistiques...</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Skeleton height="100px" width="full" />
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} w="full">
                <Skeleton height="80px" />
                <Skeleton height="80px" />
                <Skeleton height="80px" />
                <Skeleton height="80px" />
              </SimpleGrid>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  // Retourner la modale complète avec les statistiques
  return (
    <StatsModal 
      isOpen={isOpen}
      onClose={onClose}
      stats={stats}
      user={user}
    />
  );
};


// Liste des parties récentes (client-side fetch indépendant pour éviter de complexifier le hook principal)
const RecentGamesList: React.FC<{ user: any }> = ({ user }) => {
  const [games, setGames] = useState<RecentGame[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user?.uid) { setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        const token = await user.getIdToken();
        const res = await fetch(`${API_URL}/api/user/recent-games?limit=10`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            setGames(data.games || []);
          } else {
            setGames([]);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Impossible de charger les parties récentes');
          setGames([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const formatDate = (s: string) => new Date(s).toLocaleString('fr-FR');
  const pct = (a: number, b: number) => b > 0 ? Math.round((a / b) * 100) : 0;

  if (loading) {
    return (
      <VStack spacing={3} w="full">
        <Skeleton height="80px" w="full" />
        <Skeleton height="80px" w="full" />
        <Skeleton height="80px" w="full" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" w="full">
        <AlertIcon />
        <Text fontSize="sm">{error}</Text>
      </Alert>
    );
  }

  if (!games || games.length === 0) {
    return (
      <Card w="full">
        <CardBody textAlign="center" py={8}>
          <Icon as={FaGamepad} boxSize={12} color="gray.300" mb={4} />
          <Text color="gray.500">Aucune partie récente</Text>
          <Text fontSize="sm" color="gray.400" mt={2}>Jouez une partie pour remplir l'historique</Text>
        </CardBody>
      </Card>
    );
  }

  return (
    <VStack spacing={3} w="full">
      {games.map((g, idx) => {
        const accuracy = pct(g.correct_answers || 0, g.total_questions || 0);
        const isWin = g.won === true || g.won === 1;
        return (
          <Card key={idx} w="full" variant="outline">
            <CardBody>
              <HStack align="start" spacing={4} wrap="wrap">
                <VStack minW="80px" spacing={1} align="center">
                  <Badge colorScheme={isWin ? 'green' : 'red'} variant="solid" borderRadius="full" px={3}>
                    {isWin ? 'Victoire' : 'Défaite'}
                  </Badge>
                  <Text fontSize="xs" color="gray.500">{formatDate(g.played_at)}</Text>
                </VStack>

                <HStack spacing={6} flex={1} wrap="wrap">
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold">Mode</Text>
                    <Text fontSize="sm" color="gray.600">{g.game_mode === 'multiplayer' ? 'Multijoueur' : 'Local'} • {g.game_type === 'public' ? 'Public' : 'Privé'}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold">Durée</Text>
                    <Text fontSize="sm" color="gray.600">{g.duration_minutes} min</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold">Précision</Text>
                    <HStack>
                      <Text fontSize="sm" color="gray.600">{g.correct_answers}/{g.total_questions}</Text>
                      <Badge colorScheme={accuracy >= 80 ? 'green' : accuracy >= 60 ? 'yellow' : 'red'}>{accuracy}%</Badge>
                    </HStack>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold">Série max</Text>
                    <Text fontSize="sm" color="gray.600">{g.max_streak}</Text>
                  </VStack>
                  <VStack spacing={0} align="start">
                    <Text fontWeight="bold">Catégories</Text>
                    <Text fontSize="sm" color="gray.600">{g.categories_completed}</Text>
                  </VStack>
                </HStack>

                <HStack spacing={4} ml="auto">
                  <Badge colorScheme="purple" variant="subtle">XP +{g.xp_gained}</Badge>
                  <Badge colorScheme="blue" variant="subtle">Pouvoirs {g.mystery_attacks_used}</Badge>
                  <Badge colorScheme="green" variant="subtle">Réussis {g.mystery_attacks_successful}</Badge>
                  <Badge colorScheme="gray" variant="subtle">Boosts {g.power_ups_used}</Badge>
                </HStack>
              </HStack>
            </CardBody>
          </Card>
        );
      })}
    </VStack>
  );
};

// Server-backed SyncDuel recent matches for the current user
const ServerSyncDuelRecents: React.FC<{ user: any }> = ({ user }) => {
  const [items, setItems] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!user?.uid) { setItems([]); setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        const token = await user.getIdToken();
        const res = await fetch(`${API_URL}/api/syncduel/recent?limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            setItems(data.matches || []);
          } else {
            setItems([]);
          }
        }
      } catch (e) {
        if (!cancelled) { setError('Impossible de charger les duels'); setItems([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user?.uid]);

  if (loading) {
    return (
      <VStack spacing={3} w="full">
        <Skeleton height="80px" w="full" />
        <Skeleton height="80px" w="full" />
        <Skeleton height="80px" w="full" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" w="full"><AlertIcon /><Text fontSize="sm">{error}</Text></Alert>
    );
  }

  if (!items || items.length === 0) {
    return <Text color="gray.500">Aucun duel enregistré</Text>;
  }

  return (
    <VStack spacing={4} align="stretch">
      {items.map((m, idx) => {
        const left = m.player1_name ? { name: m.player1_name, id: '1', score: m.player1_score, correct: m.player1_correct, answered: m.player1_answered } : null;
        const right = m.player2_name ? { name: m.player2_name, id: '2', score: m.player2_score, correct: m.player2_correct, answered: m.player2_answered } : null;
        const winnerUserId = m.winner_user_id;
        const isLeftWinner = left && m.player1_user_id && m.player1_user_id === winnerUserId;
        const isRightWinner = right && m.player2_user_id && m.player2_user_id === winnerUserId;
  const isRandom = !(m?.category_name || m?.category_key);
  const badgeEmoji = isRandom ? '🎲' : (m?.category_emoji || '🎮');
  const badgeText = isRandom ? 'Aléatoire' : (m?.category_name || m?.category_key || '—');
        return (
          <Card key={idx} variant="outline">
            <CardBody>
              <HStack justify="space-between" mb={3} flexWrap="wrap" gap={3}>
                <HStack>
      <Badge colorScheme="purple">{badgeEmoji} {badgeText}</Badge>
                  <Text fontSize="xs" color="gray.500">{m.ended_at ? new Date(m.ended_at).toLocaleString('fr-FR') : ''}</Text>
                </HStack>
                <Badge colorScheme="gray" variant="subtle">{m.code || m.room_code}</Badge>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                <Box p={3} borderRadius="md" border="1px solid" borderColor="gray.600">
                  <HStack justify="space-between">
                    <HStack>
                      <Text fontWeight="bold">{left?.name || '—'}</Text>
                      {isLeftWinner && <Icon as={FaCrown} color="yellow.400" title="Vainqueur" />}
                    </HStack>
                    <Badge colorScheme="yellow">{left?.score ?? 0} pts</Badge>
                  </HStack>
                  <HStack mt={2} spacing={3}>
                    <Badge colorScheme="green">{left?.correct ?? 0} justes</Badge>
                    <Badge>{left?.answered ?? 0} réponses</Badge>
                  </HStack>
                </Box>
                <Box p={3} borderRadius="md" border="1px solid" borderColor="gray.600">
                  <HStack justify="space-between">
                    <HStack>
                      <Text fontWeight="bold">{right?.name || '—'}</Text>
                      {isRightWinner && <Icon as={FaCrown} color="yellow.400" title="Vainqueur" />}
                    </HStack>
                    <Badge colorScheme="yellow">{right?.score ?? 0} pts</Badge>
                  </HStack>
                  <HStack mt={2} spacing={3}>
                    <Badge colorScheme="green">{right?.correct ?? 0} justes</Badge>
                    <Badge>{right?.answered ?? 0} réponses</Badge>
                  </HStack>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        );
      })}
    </VStack>
  );
};

export default PlayerStats;
